import React, { useRef } from 'react'
import { SnackbarProvider } from 'notistack'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircle from 'components/material-symbol/icons/CheckCircle'
import ErrorOutline from 'components/material-symbol/icons/ErrorOutline'
import Info from 'components/material-symbol/icons/Info'
import Warning from 'components/material-symbol/icons/Warning'
import Close from 'components/material-symbol/icons/Close'

const SNACKBAR_FOREGROUND = '#172024'

const useSnackbarStyles = makeStyles({
  contentRoot: {
    borderRadius: 12,
    boxShadow:
      '0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.3) !important',
    fontSize: '0.875rem',
    lineHeight: '20px',
    fontWeight: 400,
    minHeight: 48,
    minWidth: 288,
    maxWidth: 568,
    padding: '0 20px !important',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  variantSuccess: {
    backgroundColor: '#BEF1BB !important',
    color: '#00210B !important',
  },
  variantError: {
    backgroundColor: '#FFBCBC !important',
    color: '#410002 !important',
  },
  variantWarning: {
    backgroundColor: '#FFF8E1 !important',
    color: `${SNACKBAR_FOREGROUND} !important`,
  },
  variantInfo: {
    backgroundColor: '#E3F2FD !important',
    color: `${SNACKBAR_FOREGROUND} !important`,
  },
  message: {
    fontWeight: 400,
    alignItems: 'center',
    gap: 12,
    padding: '0 !important',
    flex: 1,
    display: 'flex',
  },
  lessPadding: {
    paddingLeft: '20px !important',
  },
  action: {
    color: 'inherit !important',
    alignItems: 'center',
    paddingLeft: 16,
    marginRight: -8,
  },
  closeButton: {
    padding: 4,
    color: 'inherit',
  },
})

const snackbarIconStyle = {
  fontSize: 20,
  width: 20,
  height: 20,
  flexShrink: 0,
}

const iconVariant = {
  success: <CheckCircle style={{ ...snackbarIconStyle, color: '#00210B' }} />,
  error: <ErrorOutline style={{ ...snackbarIconStyle, color: '#410002' }} />,
  warning: <Warning style={{ ...snackbarIconStyle, color: '#ED6C02' }} />,
  info: <Info style={{ ...snackbarIconStyle, color: '#0288D1' }} />,
}

const defaultAnchorOrigin = { vertical: 'top', horizontal: 'center' }

const AppSnackbarProvider = ({
  children,
  anchorOrigin = defaultAnchorOrigin,
  ...props
}) => {
  const classes = useSnackbarStyles()
  const snackbarProviderRef = useRef(null)

  const closeAction = key => (
    <IconButton
      className={classes.closeButton}
      size="small"
      aria-label="Close notification"
      onClick={() => snackbarProviderRef.current?.closeSnackbar(key)}
    >
      <Close fontSize="small" />
    </IconButton>
  )

  return (
    <SnackbarProvider
      ref={snackbarProviderRef}
      anchorOrigin={anchorOrigin}
      classes={{
        contentRoot: classes.contentRoot,
        variantSuccess: classes.variantSuccess,
        variantError: classes.variantError,
        variantWarning: classes.variantWarning,
        variantInfo: classes.variantInfo,
        message: classes.message,
        lessPadding: classes.lessPadding,
        action: classes.action,
      }}
      iconVariant={iconVariant}
      action={closeAction}
      {...props}
    >
      {children}
    </SnackbarProvider>
  )
}

export default AppSnackbarProvider
